import { FormEvent } from 'react';
import { AxiosResponse } from 'axios';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAulaService } from '../../../services/aula.service';
import { useGlobalContext } from '../../../context/GlobalContext';
import { useNotificationService } from '../../../services/notification.service';
import useBackClassroomPage from './use-back-classroom-page.hook';
import useEnumMapper
  from '../components/student-accordion/hooks/use-convert-enum-to-number.hook';
import useFindRelatorioFrequenciaSkill
  from '../components/student-accordion/hooks/use-find-relatorio-frequencia-skill.hook';
import { listAccordionFormStore } from '../store/list-accordion-form.store';
import {
  ICreateFrequenciaSkillDTO
} from '../../../models/happy-code-api.model';
import {
  FrequenciaTipoEnum
} from '../../../enum/lancamento-aula/frequencia-tipo.enum';
import {
  IListAccordionForm
} from '../interfaces/list-accordion-form.interface';

interface UseGenericFrequenciaSkillFormHookProps {
  isEdit?: boolean;
}

export const useGenericFrequenciaSkillFormHook = ({
  isEdit
}: UseGenericFrequenciaSkillFormHookProps) => {
  const { id } = useParams();
  const aulaService = useAulaService();
  const { setIsGlobalLoading } = useGlobalContext();
  const notificationService = useNotificationService();
  const { backClassroomPage } = useBackClassroomPage();
  const { frequenciaTipo } = useEnumMapper();
  const { getRelatorioFrequenciaSkill } = useFindRelatorioFrequenciaSkill();
  const { t } = useTranslation(['gestao_de_aulas']);

  const {
    listAccordionForm,
    setAccordionFormDisabled,
    openNextAccordion,
    resetAccordionForm
  } = listAccordionFormStore();

  const submitForm = (event: FormEvent<HTMLFormElement>, accordion: () => IListAccordionForm) => {
    event.preventDefault();
    setIsGlobalLoading(true);

    const currentAccordion = accordion();
    const idMatricula = currentAccordion.id;

    if (!currentAccordion?.form) return;

    const payload = buildPayload(currentAccordion, idMatricula);

    const serviceMethod = isEdit
      ? aulaService.updateRelatorioFrequenciaSkill
      : aulaService.createRelatorioFrequenciaSkill;

    handleSubmit(serviceMethod, payload, idMatricula, isEdit);
  };

  const handleSubmit = (
    serviceMethod: (payload: ICreateFrequenciaSkillDTO) => Promise<AxiosResponse<any, any>>,
    payload: ICreateFrequenciaSkillDTO,
    idMatricula: number,
    isEditMode: boolean
  ) => {
    const successDescription = isEditMode
      ? t('gestao_de_aulas:frequencyUpdatedSuccess')
      : t('gestao_de_aulas:frequencyCreatedSuccess');

    const errorDescription = isEditMode
      ? t('gestao_de_aulas:frequencyUpdatedError')
      : t('gestao_de_aulas:frequencyCreatedError');

    serviceMethod(payload)
      .then(() => processSuccess(successDescription, idMatricula))
      .catch(() => notificationService({
        type: 'error',
        message: t('gestao_de_aulas:taughtContent'),
        description: errorDescription
      }))
      .finally(() => setIsGlobalLoading(false));
  };

  const processSuccess = (description: string, idMatricula: number) => {
    setAccordionFormDisabled(idMatricula);
    notificationService({
      type: 'success',
      message: t('gestao_de_aulas:taughtContent'),
      description
    });

    const isLastAccordion = listAccordionForm?.[listAccordionForm.length - 1]?.id === idMatricula;
    if (!isEdit && isLastAccordion) {
      backClassroomPage();
      return;
    }

    openNextAccordion(idMatricula);
    resetAccordionForm(idMatricula);
    getRelatorioFrequenciaSkill(idMatricula);
  };

  const buildPayload = (currentAccordion: IListAccordionForm, idMatricula: number): ICreateFrequenciaSkillDTO => {
    const observacao = currentAccordion.form?.observacao?.trim() || '';

    return {
      idAula: parseInt(id, 10),
      idMatricula,
      frequenciaDTO: {
        idFrequenciaTipo: frequenciaTipo.toNumber(currentAccordion.form.presenca),
        idFrequenciaTarefa: 4,
        frequencia: currentAccordion.form.presenca !== FrequenciaTipoEnum.AUSENTE,
        reforco: false,
        descricaoAlunoAula: observacao
      },
      alunoHabilidadeDTO: [],
      alunoCompetenciaDTO: [],
      projetoDTO: {
        link: currentAccordion.form.projetoLink,
        numeroNota: currentAccordion.form.projetoValue,
        nomeArquivo: currentAccordion.form.projetoNomeArquivo
      },
    };
  };

  return {
    submitForm
  };
};
